import { EFieldMatch, EFieldValidate, IFieldMatchConfig, IFilterConfig, IFilterConfigs, IFilterDateLogic, SingleOrArray } from './types'

export const filterMenuClasses = {
  list: 'FilterMenu-list',
  item: 'FilterMenu-item'
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const convertFormDataToJson = function <TModel>(form: FormData): Partial<TModel> {
  const data = Array.from(form as any).reduce<Partial<TModel>>((a: unknown | any, b: any) => {
    if (!a[b[0]]) {
      a[b[0]] = b[1]
    } else if (Array.isArray(a[b[0]])) {
      a[b[0]].push(b[1])
    } else {
      a[b[0]] = [a[b[0]], b[1]]
    }
    return a
  }, {} as any) as any

  Object.keys(data).forEach((key) => {
    const value = data[key]
    if (typeof value === 'string' && (value.toString().toLocaleLowerCase() === 'true' || value.toString().toLocaleLowerCase() === 'false')) {
      data[key] = value.toString().toLocaleLowerCase() === 'true'
    }
  })
  return data
}

const isYoutubeLink = (val: string) => /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//.test(val)

const isGenericLink = (val: string) => /^https?:\/\/[^ ]+$/.test(val)

const isGuid = (val: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val)

export const getFieldsMatches = <T>(fields?: IFilterConfigs<T>, keyword?: string): IFilterConfigs<T> => {
  const trimmed = keyword?.trim().toLowerCase() ?? ''
  if (!trimmed) return {}

  const matched: IFilterConfigs<T> = {}

  for (const key in fields) {
    const field = key as keyof IFilterConfigs<T>
    const config = fields[field]
    if (!config?.quickSearchMatch) continue
    const temp: SingleOrArray<IFieldMatchConfig> = config.quickSearchMatch.rules ?? []
    const rules = Array.isArray(temp) ? temp : [temp]

    let visible = false

    for (const rule of rules) {
      switch (rule.rule) {
        case EFieldMatch.AlwaysVisible:
          visible = true
          break
        case EFieldMatch.MatchOnly:
          if ('match' in rule && typeof rule.match === 'function') {
            if (rule.match(trimmed)) visible = true
          }
          break
        case EFieldMatch.LinkYoutube:
          if (isYoutubeLink(trimmed)) visible = true
          break
        case EFieldMatch.Link:
          if (isGenericLink(trimmed)) visible = true
          break
        case EFieldMatch.Guid:
          if (isGuid(trimmed)) visible = true
          break
        default:
          break
      }

      if (visible) break
    }

    if (visible) matched[field] = config
  }

  return matched
}

export const getFieldsValidation = <T>(fields?: IFilterConfigs<T>): IFilterConfigs<T> => {
  if (!fields) return {} as IFilterConfigs<T>

  const filteredFields: IFilterConfigs<T> = {}

  for (const [key, config] of Object.entries(fields) as [keyof T, IFilterConfig][]) {
    // Default to true if no validation is provided
    if (config && config.filterable !== false) {
      filteredFields[key] = config
    }
  }

  return filteredFields
}

export const filterFieldValidated = <T>(value: string, fieldKey: keyof T, filterField?: IFilterConfig): { error: boolean; message?: string } => {
  const trimmed = value.trim().toLowerCase()
  if (!trimmed) return { error: true, message: `The ${fieldKey.toString()} is required ` }
  const rules = filterField?.validation?.rules ?? []
  const rulesValidation = Array.isArray(rules) ? rules : [rules]
  const obj: { error: boolean; message?: string } = { error: false }
  for (const validateObj of rulesValidation) {
    switch (validateObj.rule) {
      case EFieldValidate.LinkYoutube:
        if (!isYoutubeLink(trimmed)) {
          obj.error = true
          obj.message = `The ${filterField?.label ?? fieldKey.toString()} must be a valid YouTube link`
        }
        break

      case EFieldValidate.Link:
        if (!isGenericLink(trimmed)) {
          obj.error = true
          obj.message = `The ${filterField?.label ?? fieldKey.toString()} must be a valid URL link`
        }
        break

      case EFieldValidate.Custom:
        if (typeof validateObj.validator === 'function' && !validateObj.validator(trimmed)) {
          obj.error = true
          obj.message = `The ${filterField?.label ?? fieldKey.toString()} is invalid`
        }
        break
      default:
        break
    }
    if (obj.error) break
  }
  return obj
}

export const mapDateLogic: { [key in IFilterDateLogic]: string } = {
  before: '<',
  after: '>'
}

export const mapDateLogicText: { [key in IFilterDateLogic]: string } = {
  before: 'Before',
  after: 'After'
}
